import React from 'react';
import api from '../services/api';
import { localDate } from '../utils/localDate';

// ─────────────────────────────────────────────────────────────────────────────
// The gate register (mig 199) — read-only.
//
// One row per movement across the gate, with the documents that arrived on it
// nested underneath. An entry covering two receipts renders as ONE entry with
// two documents, not as two rows: that is the fact the register records, and
// flattening it would undo the whole point of resolve-or-create.
//
// Nothing here creates or edits an entry. Entries are born on the receiving and
// gate-pass paths; a register you can type into directly stops being a
// transcription of the guard's book and becomes a second, competing book.
// ─────────────────────────────────────────────────────────────────────────────
const card = { background: '#fff', borderRadius: 10, padding: 18, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' };
const inp = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 };

// The LOCAL date of the 1st (16 Oct 2026). toISOString() of a local midnight is the previous day in IST, so this
// read the last day of the previous month, every day of the year.
const monthStart = () => { const d = new Date(); return localDate(new Date(d.getFullYear(), d.getMonth(), 1)); };
const today = () => new Date().toISOString().slice(0, 10);
const fmtDT = (t) => t ? new Date(t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const DIR = {
  in:  { label: '⬅ IN',  bg: '#E7F5EC', c: '#166534' },
  out: { label: 'OUT ➡', bg: '#EEF0F7', c: '#3B3A8F' },
};
const KIND = {
  'GRN': '#166534', 'Job-work GRN': '#0891b2',
  'Gate pass (out)': '#3B3A8F', 'Gate pass return': '#9A6B00',
};

export default function GateRegister() {
  // The window defaults to the current month, and that default is load-bearing rather
  // than cosmetic — see the gap panel's note. Widening it weakens the gap check.
  const [from, setFrom] = React.useState(monthStart());
  const [to, setTo] = React.useState(today());
  const [dir, setDir] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [gaps, setGaps] = React.useState([]);
  const [maxSpan, setMaxSpan] = React.useState(2000);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');

  // WHICH BOOK. RSR and Deroi keep separate physical registers, and this list is company-scoped
  // server-side — so without a picker it silently shows the viewer's HOME company, and every Deroi
  // operator's home is RSR. A register view that does not name its book is a wrong answer
  // delivered confidently.
  const [companies, setCompanies] = React.useState([]);
  const [canSwitch, setCanSwitch] = React.useState(false);
  const [company, setCompany] = React.useState('');
  const H = React.useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);
  React.useEffect(() => {
    api.get('/gate-register/companies')
      .then(r => { setCompanies(r.data.data || []); setCanSwitch(r.data.can_switch);
                   setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || ''); })
      .catch(() => {});
  }, []);

  const load = React.useCallback(() => {
    if (!company) return;               // never ask before we know whose book to ask for
    setLoading(true); setErr('');
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (dir) qs.set('direction', dir);
    const win = new URLSearchParams();
    if (from) win.set('from', from);
    if (to) win.set('to', to);
    Promise.all([
      api.get('/gate-register?' + qs.toString(), H()),
      api.get('/gate-register/gaps?' + win.toString(), H()),
    ]).then(([a, b]) => {
      setRows(a.data.data || []);
      setGaps(b.data.data || []);
      if (b.data.max_span) setMaxSpan(b.data.max_span);
    }).catch(e => setErr(e.response?.data?.error || 'Failed to load the register'))
      .finally(() => setLoading(false));
  }, [from, to, dir, company, H]);
  React.useEffect(() => { load(); }, [load]);

  const anyMissing = gaps.some(g => (g.missing_count || 0) > 0 || g.span_too_wide);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px', color: '#1a1a2e' }}>🚧 Gate Register</h2>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#888' }}>
        The security guard's register, as transcribed. Each entry is one movement across the gate;
        the documents booked against it are listed underneath. Read-only — entries are created when
        a receipt or gate pass is recorded, never here.
      </p>

      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {/* First control on the row, because which BOOK you are reading outranks which dates.
            Rendered disabled rather than hidden when the viewer cannot switch, so the screen
            always names its register instead of leaving it to be assumed. */}
        <div><label style={lbl}>Register</label>
          <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)}
            title={canSwitch ? "Whose gate register is shown" : "You can only see your own company's register"}
            style={{ ...inp, fontWeight: 700, color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div><label style={lbl}>From</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} /></div>
        <div><label style={lbl}>To</label><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} /></div>
        <div><label style={lbl}>Direction</label>
          <select value={dir} onChange={e => setDir(e.target.value)} style={inp}>
            <option value="">Both</option><option value="in">In</option><option value="out">Out</option>
          </select></div>
        <button onClick={load} style={{ padding: '8px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Refresh</button>
        {(from !== monthStart() || to !== today() || dir) && (
          <button onClick={() => { setFrom(monthStart()); setTo(today()); setDir(''); }}
            style={{ padding: '8px 14px', background: '#f0f2f8', color: '#444', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Clear</button>
        )}
      </div>

      {err && <div style={{ ...card, background: '#FCEBEB', color: '#A32D2D', fontSize: 13 }}>{err}</div>}

      {/* ── Gap panel ──────────────────────────────────────────────────────────
          The only correctness check a transcribed sequence permits. It cannot tell
          you a number we hold is real; it can tell you one is absent. Every limit
          is stated on screen rather than left for the reader to discover. */}
      <div style={{ ...card, background: anyMissing ? '#FBF7EF' : '#fff', border: anyMissing ? '1px solid #f6d195' : '1px solid #eee' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: anyMissing ? '#9A6B00' : '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
          Missing numbers
        </div>
        {gaps.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888' }}>No numbered entries in this window — nothing to sequence.</div>
        ) : gaps.map((g, i) => (
          <div key={i} style={{ marginBottom: i < gaps.length - 1 ? 12 : 0, fontSize: 13 }}>
            <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: (DIR[g.direction] || DIR.in).bg, color: (DIR[g.direction] || DIR.in).c }}>
              {(DIR[g.direction] || DIR.in).label}</span>
            {g.company_name && <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>{g.company_name}</span>}
            <span style={{ marginLeft: 8, color: '#555' }}>
              {g.numeric_count} numbered {g.lo != null && <>· range <strong>{g.lo}–{g.hi}</strong></>}
            </span>
            <div style={{ marginTop: 4 }}>
              {g.span_too_wide ? (
                <span style={{ color: '#A32D2D', fontWeight: 600 }}>
                  Span {g.span} exceeds {maxSpan} — not enumerated. Almost certainly a mis-keyed number.
                </span>
              ) : (g.missing_count || 0) === 0 ? (
                <span style={{ color: '#166534', fontWeight: 600 }}>✓ Unbroken — no interior number absent.</span>
              ) : (
                <span style={{ color: '#A32D2D' }}>
                  <strong>{g.missing_count} absent:</strong>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{(g.missing || []).join(', ')}</span>
                </span>
              )}
            </div>
            {g.unsequenceable_count > 0 && (
              <div style={{ marginTop: 3, fontSize: 12, color: '#9A6B00' }}>
                {g.unsequenceable_count} entry/entries cannot be sequenced (not purely numeric):{' '}
                <span style={{ fontFamily: 'monospace' }}>{(g.unsequenceable || []).join(', ')}</span>
              </div>
            )}
            <div style={{ marginTop: 3, fontSize: 11, color: '#999' }}>{g.note}</div>
          </div>
        ))}
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #e6e0d4', fontSize: 11, color: '#999' }}>
          Counted within the selected window only. That is deliberate: when the guard starts a new
          book at 1, a scan across all history would report every number in the old book as missing.
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
          Entries ({rows.length})
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Entry no', 'Direction', 'Date & time', 'Party', 'Vehicle', 'Carried by', 'Documents'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa' }}>
                No entries in this window. An entry appears here once someone records a receipt or a
                gate pass with the register number.
              </td></tr>
            )}
            {!loading && rows.map(r => {
              const d = DIR[r.direction] || DIR.in;
              return (
                <tr key={r.id}>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#9A6B00', fontSize: 13 }}>{r.entry_no}</td>
                  <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: d.bg, color: d.c }}>{d.label}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDT(r.entry_at)}
                    {/* Every row reads TRUE today. The flag stops being interesting only once the
                        guard keys entries himself — not before, so it is shown, not assumed. */}
                    {r.is_transcribed && <div style={{ fontSize: 10, color: '#aaa' }}>transcribed</div>}</td>
                  <td style={td}>{r.party || <span style={{ color: '#ccc' }}>—</span>}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.vehicle_no || <span style={{ color: '#ccc' }}>—</span>}</td>
                  <td style={td}>{r.carried_by || <span style={{ color: '#ccc' }}>—</span>}</td>
                  <td style={td}>
                    {(r.docs || []).length === 0
                      ? <span style={{ color: '#ccc' }}>none linked</span>
                      : r.docs.map((x, i) => (
                        <div key={i} style={{ marginBottom: 2, whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: KIND[x.kind] || '#555' }}>{x.kind}</span>
                          <span style={{ fontFamily: 'monospace', marginLeft: 6, fontWeight: 600 }}>{x.ref}</span>
                          {x.ctx && <span style={{ color: '#888', marginLeft: 6 }}>· {x.ctx}</span>}
                        </div>
                      ))}
                    {(r.docs || []).length > 1 && (
                      <div style={{ fontSize: 10, color: '#9A6B00', marginTop: 3 }}>
                        one entry · {r.docs.length} movements booked against it
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
